import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { completeOnboarding } from '../api/auth';

interface OnboardingState {
  completed: boolean;
  accountHomeStep: number; // 0 = not started, 1-5 = current step, 6 = done
  gameTooltipStep: number; // 1 = show step 1, 2 = show step 2, 3 = done
  resultsTooltipShown: boolean;
  levelUpTooltipShown: boolean;
}

interface OnboardingContextType {
  state: OnboardingState;
  shouldShowTooltip: (page: string, step: number) => boolean;
  dismissStep: (page: string, step: number) => void;
  isOnboardingActive: (page: string) => boolean;
}

const getStorageKey = (userId: string) => `onboarding_bonus_tooltips_${userId}`;

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

interface BonusTooltips {
  gameStep: number; // 1 = not started, 2 = step 1 seen, 3 = done
  results: boolean;
  levelUp: boolean;
}

const DEFAULT_BONUS: BonusTooltips = { gameStep: 1, results: false, levelUp: false };

function loadBonusTooltips(userId: string | undefined): BonusTooltips {
  if (!userId) return { ...DEFAULT_BONUS };
  try {
    const stored = localStorage.getItem(getStorageKey(userId));
    if (stored) {
      const parsed = JSON.parse(stored);
      // Handle migration from old format (game: boolean) to new (gameStep: number)
      if ('game' in parsed && typeof parsed.game === 'boolean') {
        return { gameStep: parsed.game ? 3 : 1, results: parsed.results, levelUp: parsed.levelUp };
      }
      return parsed;
    }
  } catch { /* ignore */ }
  return { ...DEFAULT_BONUS };
}

function saveBonusTooltips(userId: string, data: BonusTooltips) {
  localStorage.setItem(getStorageKey(userId), JSON.stringify(data));
}

export const OnboardingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();

  const [state, setState] = useState<OnboardingState>({
    completed: true, // default to true until we know otherwise
    accountHomeStep: 0,
    gameTooltipStep: DEFAULT_BONUS.gameStep,
    resultsTooltipShown: DEFAULT_BONUS.results,
    levelUpTooltipShown: DEFAULT_BONUS.levelUp,
  });

  // Initialize from user data
  useEffect(() => {
    if (!user) return;

    const bonus = loadBonusTooltips(user.id);

    if (user.onboarding_completed) {
      setState({
        completed: true,
        accountHomeStep: 6, // done
        gameTooltipStep: bonus.gameStep,
        resultsTooltipShown: bonus.results,
        levelUpTooltipShown: bonus.levelUp,
      });
    } else {
      setState({
        completed: false,
        accountHomeStep: 1, // start at step 1
        gameTooltipStep: bonus.gameStep,
        resultsTooltipShown: bonus.results,
        levelUpTooltipShown: bonus.levelUp,
      });
    }
  }, [user?.id, user?.onboarding_completed]);

  const shouldShowTooltip = useCallback((page: string, step: number): boolean => {
    if (!user) return false;

    switch (page) {
      case 'accountHome':
        return !state.completed && state.accountHomeStep === step;
      case 'game':
        return state.gameTooltipStep === step;
      case 'results':
        return !state.resultsTooltipShown && step === 1;
      case 'levelUp':
        return !state.levelUpTooltipShown && step === 1;
      default:
        return false;
    }
  }, [user, state]);

  const dismissStep = useCallback((page: string, step: number) => {
    const userId = user?.id;
    setState((prev) => {
      const next = { ...prev };

      switch (page) {
        case 'accountHome': {
          if (step < 5) {
            next.accountHomeStep = step + 1;
          } else {
            // Step 5 dismissed — mark account home done and fire backend call
            next.accountHomeStep = 6;
            next.completed = true;
            completeOnboarding().catch(() => {
              // Best-effort; on next login the tooltip won't re-show
              // because we set completed = true in state
            });
          }
          break;
        }
        case 'game': {
          if (!userId) break;
          // Game has 2 tooltips: dismiss step 1 shows step 2, dismiss step 2 completes
          const nextGameStep = step + 1;
          next.gameTooltipStep = nextGameStep;
          const bonusGame = loadBonusTooltips(userId);
          bonusGame.gameStep = nextGameStep;
          saveBonusTooltips(userId, bonusGame);
          break;
        }
        case 'results': {
          if (!userId) break;
          next.resultsTooltipShown = true;
          const bonusResults = loadBonusTooltips(userId);
          bonusResults.results = true;
          saveBonusTooltips(userId, bonusResults);
          break;
        }
        case 'levelUp': {
          if (!userId) break;
          next.levelUpTooltipShown = true;
          const bonusLevelUp = loadBonusTooltips(userId);
          bonusLevelUp.levelUp = true;
          saveBonusTooltips(userId, bonusLevelUp);
          break;
        }
      }

      return next;
    });
  }, [user?.id]);

  const isOnboardingActive = useCallback((page: string): boolean => {
    switch (page) {
      case 'accountHome':
        return !state.completed && state.accountHomeStep >= 1 && state.accountHomeStep <= 5;
      case 'game':
        return state.gameTooltipStep <= 2;
      case 'results':
        return !state.resultsTooltipShown;
      case 'levelUp':
        return !state.levelUpTooltipShown;
      default:
        return false;
    }
  }, [state]);

  return (
    <OnboardingContext.Provider value={{ state, shouldShowTooltip, dismissStep, isOnboardingActive }}>
      {children}
    </OnboardingContext.Provider>
  );
};
